/** @jsxImportSource @emotion/react */
import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  TextField,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Snackbar,
  FormControlLabel,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import StarIcon from "@mui/icons-material/Star";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { useEventData } from "../EventDataContext";
import RedeemIcon from '@mui/icons-material/Redeem';
import SendIcon from '@mui/icons-material/Send';

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: 400,
    color: "#333",
    transition: "all 0.2s ease-in-out",
    "& fieldset": {
      border: "1px solid #E0E0E0",
    },
    "&:hover fieldset": {
      border: "1px solid #BDBDBD",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #8A5918",
      boxShadow: "0 0 0 3px rgba(138, 89, 24, 0.1)",
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
    },
  },
}));

const SectionTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
    <Box sx={{ width: '4px', height: '24px', backgroundColor: '#8A5918', mr: 1, borderRadius: '2px' }} />
    <Typography
      variant="h6"
      sx={{
        color: "#1A1A1A",
        fontWeight: 700,
        fontSize: "18px",
        textAlign: "left",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const Label = ({ children, required }) => (
  <Typography
    sx={{
      color: "#4A4A4A",
      fontWeight: 600,
      fontSize: "14px",
      mb: 1,
      textAlign: "left",
    }}
  >
    {children} {required && <span style={{ color: "#E94C4C" }}>*</span>}
  </Typography>
);

export default function MainManagementPage() {
  const { eventData, updateNestedEventData, uploadImageAndUpdateData, validationErrors } = useEventData();

  const mainData = eventData.main;
  const giftData = eventData.gift || {}; // Access gift data

  const backgroundImage = mainData.backgroundImage?.value;
  const textPosition = mainData.textPosition?.value || "left";
  const eventTitle = mainData.eventTitle?.value || "";
  const eventDescription = mainData.eventDescription?.value || "";
  const wishButtonEnabled = mainData.wishButton?.enabled?.value || false;
  const selectedIcon = mainData.wishButton?.icon?.value || "star";
  const wishButtonText = mainData.wishButton?.text?.value || "";

  // Gift Button Variables
  const giftButtonEnabled = giftData?.giftButton?.enabled?.value || false;
  const giftButtonText = giftData?.giftButton?.text?.value || "";
  const giftButtonIcon = giftData?.giftButton?.icon?.value || "star";

  const [uploadingImage, setUploadingImage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  const hasError = (fieldName) => {
    return validationErrors?.main?.[fieldName];
  };

  const getErrorMessage = (fieldName) => {
    return validationErrors?.main?.[fieldName] || '';
  };

  const hasNestedError = (section, fieldName) => {
    return validationErrors?.main?.[section]?.[fieldName];
  };

  const getNestedErrorMessage = (section, fieldName) => {
    return validationErrors?.main?.[section]?.[fieldName] || '';
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadingImage(true);
      try {
        const result = await uploadImageAndUpdateData("main", "backgroundImage", file);
        if (!result.success) {
          if (!result.error.includes('size')) {
            const reader = new FileReader();
            reader.onloadend = () => {
              updateNestedEventData("main", "backgroundImage", { value: reader.result });
            };
            reader.readAsDataURL(file);
          } else {
            setErrorMessage(result.error);
            setShowError(true);
          }
        }
      } catch (error) {
        setErrorMessage("An unexpected error occurred while uploading the image");
        setShowError(true);
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const removeImage = () => {
    updateNestedEventData("main", "backgroundImage", { value: null });
  };

  const iconOptions = [
    { value: "star", icon: <StarIcon />, label: "Star" },
    { value: "heart", icon: <FavoriteIcon />, label: "Heart" },
    { value: "thumbup", icon: <ThumbUpIcon />, label: "Thumb Up" },
    { value: "redeem", icon: <RedeemIcon />, label: "Redeem" },
    { value: "send", icon: <SendIcon />, label: "Send" },
  ];

  return (
    <Box>
      <SectionTitle>Main Image</SectionTitle>
      <Box
        sx={{
          position: "relative",
          backgroundColor: "#F9FAFB",
          border: "2px dashed #E0E0E0",
          borderRadius: "12px",
          p: 3,
          textAlign: "center",
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "#8A5918",
            backgroundColor: "#FFF8F0",
          },
        }}
      >
        {backgroundImage ? (
          <>
            <img
              src={backgroundImage}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "300px",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
            <IconButton
              onClick={removeImage}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center">
            <AttachFileIcon sx={{ color: "#9CA3AF", fontSize: 48, mb: 2 }} />
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="#4A4A4A"
              mb={0.5}
            >
              Click to upload image
            </Typography>
            <Typography variant="body2" color="#9CA3AF" mb={3}>
              SVG, PNG, JPG or GIF (max. 800KB)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              disabled={uploadingImage}
              sx={{
                color: "#8A5918",
                borderColor: "#8A5918",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#6b3b07",
                  backgroundColor: "rgba(138, 89, 24, 0.04)",
                },
              }}
            >
              {uploadingImage ? "Uploading..." : "Browse File"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploadingImage}
              />
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <SectionTitle>Event Content</SectionTitle>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <Label>Text Position</Label>
          <ToggleButtonGroup
            value={textPosition}
            exclusive
            onChange={(e, newValue) => {
              if (newValue)
                updateNestedEventData("main", "textPosition", {
                  value: newValue,
                });
            }}
            sx={{
              width: "100%",
              "& .MuiToggleButton-root": {
                flex: 1,
                color: "#666",
                borderColor: "#E0E0E0",
                textTransform: "none",
                py: 1.5,
                "&.Mui-selected": {
                  backgroundColor: "#FFF8F0",
                  color: "#8A5918",
                  borderColor: "#8A5918",
                  "&:hover": {
                    backgroundColor: "#FFF0E0",
                  },
                },
              },
            }}
          >
            <ToggleButton value="left">
              <FormatAlignLeftIcon sx={{ mr: 1 }} /> Left
            </ToggleButton>
            <ToggleButton value="center">
              <FormatAlignCenterIcon sx={{ mr: 1 }} /> Center
            </ToggleButton>
            <ToggleButton value="right">
              <FormatAlignRightIcon sx={{ mr: 1 }} /> Right
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box>
          <Label required>Event Title</Label>
          <StyledTextField
            placeholder="Enter event title"
            value={eventTitle}
            onChange={(e) =>
              updateNestedEventData("main", "eventTitle", {
                value: e.target.value,
                required: true,
              })
            }
            error={hasError("eventTitle")}
            helperText={getErrorMessage("eventTitle")}
          />
        </Box>

        <Box>
          <Label required>Event Description</Label>
          <StyledTextField
            placeholder="Enter event description"
            value={eventDescription}
            onChange={(e) =>
              updateNestedEventData("main", "eventDescription", {
                value: e.target.value,
                required: true,
              })
            }
            multiline
            rows={4}
            error={hasError("eventDescription")}
            helperText={getErrorMessage("eventDescription")}
          />
        </Box>
      </Box>

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <SectionTitle>Gift Button</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={giftButtonEnabled}
              onChange={(e) => updateNestedEventData('gift', 'giftButton', {
                ...(giftData?.giftButton || {}),
                enabled: { value: e.target.checked, required: false }
              })}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          }
          label={giftButtonEnabled ? "Enabled" : "Disabled"}
        />
      </Box>

      {giftButtonEnabled && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Label>Button Icon</Label>
            <FormControl fullWidth>
              <Select
                value={giftButtonIcon}
                onChange={(e) => updateNestedEventData('gift', 'giftButton', {
                  ...(giftData?.giftButton || {}),
                  icon: { value: e.target.value, required: false }
                })}
                IconComponent={ExpandMoreIcon}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#8A5918",
                  },
                  "& .MuiSelect-select": {
                    padding: "12px 16px",
                  },
                }}
              >
                {iconOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {option.icon}
                      <Typography>{option.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Label required={giftButtonEnabled}>Button Text</Label>
            <StyledTextField
              placeholder="e.g. Send a Gift"
              value={giftButtonText}
              onChange={(e) => updateNestedEventData('gift', 'giftButton', {
                ...(giftData?.giftButton || {}),
                text: { value: e.target.value, required: false }
              })}
              error={validationErrors?.gift?.giftButton?.text}
              helperText={validationErrors?.gift?.giftButton?.text}
            />
          </Grid>
        </Grid>
      )}

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <SectionTitle>Wish Button</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={wishButtonEnabled}
              onChange={(e) =>
                updateNestedEventData("main", "wishButton", {
                  enabled: { value: e.target.checked }
                })
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          }
          label={wishButtonEnabled ? "Enabled" : "Disabled"}
        />
      </Box>

      {wishButtonEnabled && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Label>Button Icon</Label>
            <FormControl fullWidth>
              <Select
                value={selectedIcon}
                onChange={(e) =>
                  updateNestedEventData("main", "wishButton", {
                    icon: { value: e.target.value }
                  })
                }
                IconComponent={ExpandMoreIcon}
                sx={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#BDBDBD",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#8A5918",
                  },
                  "& .MuiSelect-select": {
                    padding: "12px 16px",
                  },
                }}
              >
                {iconOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {option.icon}
                      <Typography>{option.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Label required={wishButtonEnabled}>Button Text</Label>
            <StyledTextField
              placeholder="e.g. Send a Wish"
              value={wishButtonText}
              onChange={(e) =>
                updateNestedEventData("main", "wishButton", {
                  text: { value: e.target.value }
                })
              }
              error={hasNestedError('wishButton', 'text')}
              helperText={getNestedErrorMessage('wishButton', 'text')}
            />
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
